import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../pages/Dashboard";
import Chat from "../pages/Chat";
import TeamPage from "../pages/Team";
import TeamTasksRoutes from "../pages/TeamTasks";
import TeamAgendaRoutes from "../pages/TeamAgenda";
import TeamSalesPipelineRoutes from "../pages/SalesPipeline/TeamSalesPipelineRoutes";
import TeamAwarenessPipelineRoutes from "../pages/Pipeline/TeamAwarenessPipelineRoutes";
import TeamVirtualOfficeRoutes from "../pages/VirtualOffice/TeamVirtualOfficeRoutes";
import Clients from "../pages/Clients";
import ClientDetails from "../pages/Clients/ClientDetails";
import ClientEditPage from "../pages/Clients/ClientEditPage";
import ClientNewPage from "../pages/Clients/ClientNewPage";
import Pipeline from "../pages/Pipeline";
import SalePipeline from "../pages/SalesPipeline"; 
import Products from "../pages/Products";
import PassengerCitiesPage from "../pages/PassengerCitiesPage";
import PassagensListPage from "../pages/Passagens/PassagensListPage";
import PassagensRepassesPage from "../pages/Passagens/PassagensRepassesPage";
import TicketSalesPipeline from "../pages/TicketSalesPipeline";
import Surveys from "../pages/Surveys"; 
import SurveyAnalyticsPage from "../pages/Surveys/SurveyAnalyticsPage";
import Users from "../pages/Settings/Users";
import UserOperatorAnalytics from "../pages/Settings/Users/UserOperatorAnalytics";
import Channels from "../pages/Settings/Channels";
import Types from "../pages/Settings/Types";
import Companies from "../pages/Settings/Companies";
import AutomationRules from "../pages/Settings/AutomationRules";
import PaymentMethods from "../pages/Settings/PaymentMethods";
import DeliveryMethods from "../pages/Settings/DeliveryMethods";
import Integrations from "../pages/Settings/Integrations";
import PaidAds from "../pages/Settings/PaidAds";
import UserDetails from "../pages/UserDetails";
import SearchResults from "../pages/SearchResults";
import Campaigns from "../pages/Campaigns";
import LoginPage from "../pages/LoginPage";
import SignupPage from "../pages/SignupPage";
import VerifyEmailPage from "../pages/VerifyEmailPage";
import PublicSurveyPage from "../pages/PublicSurveyPage";
import PublicDashboardPresentationPage from "../pages/PublicDashboardPresentationPage";
import PublicWebsitePage from "../pages/PublicWebsitePage";
import PublicVideoPage from "../pages/PublicVideo/PublicVideoPage";
import ClientPortalLoginPage from "../pages/ClientPortal/ClientPortalLoginPage";
import ClientPortalForgotPasswordPage from "../pages/ClientPortal/ClientPortalForgotPasswordPage";
import ClientPortalPasswordPage from "../pages/ClientPortal/ClientPortalPasswordPage";
import ClientPortalLayout from "../pages/ClientPortal/ClientPortalLayout";
import ClientPortalTicketListPage from "../pages/ClientPortal/ClientPortalTicketListPage";
import ClientPortalTicketDetailPage from "../pages/ClientPortal/ClientPortalTicketDetailPage";
import ClientPortalNewTicketPage from "../pages/ClientPortal/ClientPortalNewTicketPage";
import WebChatPage from "../pages/WebChat/WebChatPage";
import TicketFlowPage from "../pages/TicketFlow/TicketFlowPage";
import SiteWebchatEmbedPage from "../pages/WebChat/SiteWebchatEmbedPage";
import RequireClientPortal from "../components/auth/RequireClientPortal";
import ProfilePage from "../pages/ProfilePage";
import GoogleCalendarCallbackPage from "../pages/ProfilePage/GoogleCalendarCallbackPage";
import PrivateRoute from "../components/PrivateRoute";
import UserPlan from "../pages/UserPlan";
import BotFlowEditor from "../pages/Settings/Users/BotFlowEditor";
import CallPopup from "../pages/CallPopup";
import TeamPopup from "../pages/TeamPopup";
import TeamTasksPopup from "../pages/TeamTasksPopup";
import TeamAgendaPopup from "../pages/TeamAgendaPopup";
import TeamChatPopup from "../pages/TeamChatPopup";
import SalesPipelinePopup from "../pages/SalesPipelinePopup";
import AwarenessPipelinePopup from "../pages/AwarenessPipelinePopup";
import VirtualOfficePopup from "../pages/VirtualOfficePopup";
import PopupEquipeMobileRedirect from "../components/PopupEquipeMobileRedirect";
import HomeIndex from "../components/HomeIndex";
import SetupPage from "../pages/SetupPage";
import Categories from "../pages/Categories";
import Coupons from "../pages/Coupons";
import PipelineStages from "../pages/PipelineStages";
import Banners from "../pages/Banners";
import Contents from "../pages/Contents";
import ClientTags from "../pages/ClientTags";
import Positions from "../pages/Settings/Positions";
import AwarenessStages from "../pages/AwarenessStages";
import SetPassword from "../pages/SetPassword";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import AntifraudMethods from "../pages/Settings/AntifraudMethods";
import VirtualOfficeSettingsPage from "../pages/Settings/VirtualOffice";
import TimeClockSettingsPage from "../pages/Settings/TimeClock";
import MyTimeClockPage from "../pages/TimeClock/MyTimeClockPage";
import AgentsList from "../pages/Agents/AgentsList";
import AgentForm from "../pages/Agents/AgentForm";
import AgentAnalytics from "../pages/Agents/AgentAnalytics";
import ChatbotsList from "../pages/Chatbots/ChatbotsList";
import ChatbotForm from "../pages/Chatbots/ChatbotForm";
import ChatbotAnalytics from "../pages/Chatbots/ChatbotAnalytics";
import WorkspaceI18nSettings from "../pages/Settings/WorkspaceI18n";
import CustomFieldsSettings from "../pages/Settings/CustomFields";
import BillingSettingsPage from "../pages/Settings/Billing";
import PlatformXprobaseConsole from "../pages/Platform/XprobaseConsole";
import RequireMasterPlatform from "../components/auth/RequireMasterPlatform";
import RequireMaster from "../components/auth/RequireMaster";
import RequirePartnerPortal from "../components/auth/RequirePartnerPortal";
import PartnerConsole from "../pages/Partner/PartnerConsole";
import HelpPage from "../pages/Help";
import PortalScopeLoginPage from "../pages/PortalScopeLoginPage";
import PrivacyPage from "../pages/Legal/PrivacyPage";
import TermsPage from "../pages/Legal/TermsPage";
import { FEATURE_PASSAGENS_REPASSES_ENABLED } from "../constants/featureFlags";
import PlatformConfigurations from "../pages/Settings/PlatformConfigurations";

// >>> Guards por permissão (usa /me/permissions)
import Require from "../components/auth/Require";

/** `/dashboard` → `/dashboard/live` sem perder `?ws=` (master/partner). */
function DashboardIndexRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/dashboard/live", search: location.search, hash: location.hash }}
      replace
    />
  );
}

const AppRoutes = () => {
  return (
    <Routes>
      {/* Rota de login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/partner/login" element={<PortalScopeLoginPage variant="partner" />} />
      <Route path="/platform/login" element={<PortalScopeLoginPage variant="platform" />} />
      <Route path="/partner" element={<Navigate to="/partner/console" replace />} />
      <Route path="/platform" element={<Navigate to="/platform/xprobase" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/partner/forgot-password" element={<ForgotPasswordPage variant="partner" />} />
      <Route path="/platform/forgot-password" element={<ForgotPasswordPage variant="platform" />} />

      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/public/surveys/:slug" element={<PublicSurveyPage />} />
      <Route path="/public/dashboard/presentations/:token" element={<PublicDashboardPresentationPage />} />
      <Route path="/public/websites" element={<PublicWebsitePage />} />
      <Route path="/public/websites/:slug" element={<PublicWebsitePage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      <Route path="/onboarding/password" element={<SetPassword />} />

      <Route path="/portal-cliente" element={<ClientPortalLoginPage />} />
      <Route path="/portal-cliente/forgot-password" element={<ClientPortalForgotPasswordPage />} />
      <Route path="/portal-cliente/password" element={<ClientPortalPasswordPage />} />
      <Route path="/webchat/embed" element={<SiteWebchatEmbedPage />} />
      <Route path="/webchat/:token" element={<WebChatPage />} />
      <Route path="/ticket-flow" element={<TicketFlowPage />} />
      <Route path="/video/lobby/:lobbyToken" element={<PublicVideoPage mode="lobby" />} />
      <Route path="/video/:token" element={<PublicVideoPage mode="room" />} />
      <Route
        path="/portal-cliente/app"
        element={
          <RequireClientPortal>
            <ClientPortalLayout />
          </RequireClientPortal>
        }
      >
        <Route index element={<ClientPortalTicketListPage />} />
        <Route path="new" element={<ClientPortalNewTicketPage />} />
        <Route path=":id" element={<ClientPortalTicketDetailPage />} />
      </Route>

      {/* Rotas sem layout */}
      <Route
        path="/popup-call/:id"
        element={
          <PrivateRoute noLayout>
            <CallPopup />
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="team_chat" action="view">
                <TeamPopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/atendimento/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="chat" action="view">
                <TeamChatPopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/tarefas/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="team_chat" action="view">
                <TeamTasksPopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/agenda/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="team_chat" action="view">
                <TeamAgendaPopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/sales-pipeline/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="sales_pipeline" action="view">
                <SalesPipelinePopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/pipeline/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="awareness" action="view">
                <AwarenessPipelinePopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/escritorio-virtual/*"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="virtual_office" action="view">
                <VirtualOfficePopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/popup-equipe/:id"
        element={
          <PrivateRoute noLayout>
            <PopupEquipeMobileRedirect>
              <Require resource="team_chat" action="view">
                <TeamPopup />
              </Require>
            </PopupEquipeMobileRedirect>
          </PrivateRoute>
        }
      />
      <Route
        path="/setup"
        element={
          <PrivateRoute noLayout>
            <SetupPage />
          </PrivateRoute>
        }
      />

      {/* Rotas protegidas (com layout) */}
      <Route path="/" element={<PrivateRoute />}>
        {/* Index → Chat (desktop) / Equipe·Atendimento (mobile) */}
        <Route index element={<HomeIndex />} />

        {/* Páginas principais */}
        <Route
          path="chat"
          element={
            <Require resource="chat" action="view">
              <Chat />
            </Require>
          }
        />
        <Route
          path="chat/:id"
          element={
            <Require resource="chat" action="view">
              <Chat />
            </Require>
          }
        />
        <Route
          path="equipe/tarefas/*"
          element={
            <Require resource="team_chat" action="view">
              <TeamTasksRoutes />
            </Require>
          }
        />
        <Route
          path="equipe/agenda/*"
          element={
            <Require resource="team_chat" action="view">
              <TeamAgendaRoutes />
            </Require>
          }
        />
        <Route
          path="equipe/sales-pipeline/*"
          element={
            <Require resource="sales_pipeline" action="view">
              <TeamSalesPipelineRoutes />
            </Require>
          }
        />
        <Route
          path="equipe/pipeline/*"
          element={
            <Require resource="awareness" action="view">
              <TeamAwarenessPipelineRoutes />
            </Require>
          }
        />
        <Route
          path="equipe/escritorio-virtual/*"
          element={
            <Require resource="virtual_office" action="view">
              <TeamVirtualOfficeRoutes />
            </Require>
          }
        />
        <Route
          path="equipe"
          element={
            <Require resource="team_chat" action="view">
              <TeamPage />
            </Require>
          }
        />
        <Route
          path="equipe/:id"
          element={
            <Require resource="team_chat" action="view">
              <TeamPage />
            </Require>
          }
        />
        <Route path="dashboard" element={<DashboardIndexRedirect />} />
        <Route
          path="dashboard/:tab"
          element={
            <Require resource="dashboard" action="view">
              <Dashboard />
            </Require>
          }
        />
        <Route
          path="profile"
          element={
            <Require resource="profile" action="view">
              <ProfilePage />
            </Require>
          }
        />
        <Route
          path="meu-ponto"
          element={
            <Require resource="time_clock" action="view">
              <MyTimeClockPage />
            </Require>
          }
        />
        <Route
          path="profile/google-calendar/callback"
          element={
            <Require resource="profile" action="connect_google_calendar">
              <GoogleCalendarCallbackPage />
            </Require>
          }
        />
        <Route path="user-plan" element={<UserPlan />} />
        <Route
          path="clients"
          element={
            <Require resource="clients" action="view">
              <Clients />
            </Require>
          }
        />
        <Route
          path="clients/new"
          element={
            <Require resource="clients" action="manage">
              <ClientNewPage />
            </Require>
          }
        />
        <Route
          path="clients/:id/edit"
          element={
            <Require resource="clients" action="manage">
              <ClientEditPage />
            </Require>
          }
        />
        <Route
          path="clients/:id"
          element={
            <Require resource="clients" action="view">
              <ClientDetails />
            </Require>
          }
        />
        <Route
          path="pipeline"
          element={
            <Require resource="awareness" action="view">
              <Pipeline />
            </Require>
          }
        />

        {/* >>> AQUI: duas rotas, com e sem ID */}
        <Route
          path="sales-pipeline"
          element={
            <Require resource="sales_pipeline" action="view">
              <SalePipeline />
            </Require>
          }
        />
        <Route
          path="sales-pipeline/:id"
          element={
            <Require resource="sales_pipeline" action="view">
              <SalePipeline />
            </Require>
          }
        />
        {/* <<< */}

        <Route
          path="products"
          element={
            <Require resource="products" action="view">
              <Products />
            </Require>
          }
        />
        <Route
          path="passenger-cities"
          element={
            <Require resource="cities" action="view">
              <PassengerCitiesPage />
            </Require>
          }
        />
        <Route
          path="passagens"
          element={
            <Require resource="passagens" action="view">
              <PassagensListPage />
            </Require>
          }
        />
        {FEATURE_PASSAGENS_REPASSES_ENABLED ? (
          <Route
            path="passagens/repasses"
            element={
              <Require resource="passagens" action="view">
                <PassagensRepassesPage />
              </Require>
            }
          />
        ) : null}
        <Route
          path="passagens/pipeline"
          element={
            <Require resource="passagens" action="view">
              <TicketSalesPipeline />
            </Require>
          }
        />
        <Route
          path="agents"
          element={
            <Require resource="agents" action="view">
              <AgentsList />
            </Require>
          }
        />
        <Route
          path="agents/new"
          element={
            <Require resource="agents" action="create">
              <AgentForm />
            </Require>
          }
        />
        <Route
          path="agents/:id"
          element={
            <Require resource="agents" action="view">
              <AgentForm />
            </Require>
          }
        />
        <Route
          path="agents/:id/analytics"
          element={
            <Require resource="agents" action="view">
              <AgentAnalytics />
            </Require>
          }
        />
        <Route
          path="chatbots"
          element={
            <Require resource="chatbot" action="view">
              <ChatbotsList />
            </Require>
          }
        />
        <Route
          path="chatbots/new"
          element={
            <Require resource="chatbot" action="create">
              <ChatbotForm />
            </Require>
          }
        />
        <Route
          path="chatbots/:id"
          element={
            <Require resource="chatbot" action="view">
              <ChatbotForm />
            </Require>
          }
        />
        <Route
          path="chatbots/:id/analytics"
          element={
            <Require resource="chatbot" action="view">
              <ChatbotAnalytics />
            </Require>
          }
        />
        <Route
          path="surveys"
          element={
            <Require resource="surveys" action="view">
              <Surveys />
            </Require>
          }
        />
        <Route
          path="surveys/:id/analytics"
          element={
            <Require resource="surveys" action="view">
              <SurveyAnalyticsPage />
            </Require>
          }
        />
        <Route
          path="campaigns"
          element={
            <Require resource="campaigns" action="view">
              <Campaigns />
            </Require>
          }
        />
        <Route
          path="campaigns/whatsapp"
          element={
            <Require resource="campaigns" action="view">
              <Campaigns />
            </Require>
          }
        />
        <Route
          path="campaigns/whatsapp/:id"
          element={
            <Require resource="campaigns" action="view">
              <Campaigns />
            </Require>
          }
        />
        <Route
          path="campaigns/:id"
          element={
            <Require resource="campaigns" action="view">
              <Campaigns />
            </Require>
          }
        />
        <Route
          path="user-details/:userId"
          element={
            <Require resource="users" action="view">
              <UserDetails />
            </Require>
          }
        />
        <Route path="search-results" element={<SearchResults />} />
        <Route path="help" element={<HelpPage />} />

        {/* Configurações */}
        <Route
          path="settings/users"
          element={
            <Require resource="users" action="view">
              <Users />
            </Require>
          }
        />
        <Route
          path="settings/users/:id/analytics"
          element={
            <Require resource="users" action="view">
              <UserOperatorAnalytics />
            </Require>
          }
        />
        <Route
          path="settings/positions"
          element={
            <Require resource="positions" action="view">
              <Positions />
            </Require>
          }
        />
        <Route
          path="users/:id/bot"
          element={
            <Require resource="users" action="update">
              <BotFlowEditor />
            </Require>
          }
        />
        <Route
          path="settings/channels"
          element={
            <Require resource="channels" action="view">
              <Channels />
            </Require>
          }
        />
        <Route
          path="settings/types"
          element={
            <Require resource="types" action="view">
              <Types />
            </Require>
          }
        />
        <Route
          path="settings/companies"
          element={
            <Require resource="companies" action="view">
              <Companies />
            </Require>
          }
        />
        <Route
          path="settings/configurations"
          element={
            <RequireMaster>
              <PlatformConfigurations />
            </RequireMaster>
          }
        />
        <Route
          path="settings/chatbot"
          element={
            <Require resource="chatbot" action="view">
              <Navigate to="/chatbots" replace />
            </Require>
          }
        />
        <Route
          path="settings/automation-rules"
          element={
            <Require resource="automation_rules" action="view">
              <AutomationRules />
            </Require>
          }
        />
        <Route
          path="settings/antifraud-methods"
          element={
            <Require resource="antifraud_methods" action="view">
              <AntifraudMethods />
            </Require>
          }
        />
        <Route
          path="settings/virtual-office"
          element={
            <Require resource="virtual_office" action="view">
              <VirtualOfficeSettingsPage />
            </Require>
          }
        />
        <Route
          path="settings/time-clock"
          element={
            <Require resource="time_clock" action="view">
              <TimeClockSettingsPage />
            </Require>
          }
        />
        <Route
          path="settings/payment-methods"
          element={
            <Require resource="payment_methods" action="view">
              <PaymentMethods />
            </Require>
          }
        />
        <Route
          path="settings/client-tags"
          element={
            <Require resource="client_tags" action="view">
              <ClientTags />
            </Require>
          }
        />
        <Route
          path="settings/billing"
          element={
            <Require resource="billing" action="view">
              <BillingSettingsPage />
            </Require>
          }
        />
        <Route
          path="platform/xprobase/*"
          element={
            <RequireMasterPlatform>
              <PlatformXprobaseConsole />
            </RequireMasterPlatform>
          }
        />
        <Route
          path="partner/console/*"
          element={
            <RequirePartnerPortal>
              <PartnerConsole />
            </RequirePartnerPortal>
          }
        />
        <Route
          path="settings/delivery-methods"
          element={
            <Require resource="delivery_methods" action="view">
              <DeliveryMethods />
            </Require>
          }
        />
        <Route
          path="settings/integrations"
          element={
            <Require resource="integrations" action="view">
              <Integrations />
            </Require>
          }
        />
        <Route
          path="settings/paid-ads"
          element={
            <Require resource="paid_ads" action="view">
              <PaidAds />
            </Require>
          }
        />
        <Route
          path="settings/custom-fields"
          element={
            <Require resource="types" action="view">
              <CustomFieldsSettings />
            </Require>
          }
        />
        <Route
          path="settings/workspace-language"
          element={
            <Require resource="workspace_i18n" action="view">
              <WorkspaceI18nSettings />
            </Require>
          }
        />

        {/* Catálogos adicionais */}
        <Route
          path="categories"
          element={
            <Require resource="categories" action="view">
              <Categories />
            </Require>
          }
        />
        <Route
          path="coupons"
          element={
            <Require resource="coupons" action="view">
              <Coupons />
            </Require>
          }
        />
        <Route
          path="banners"
          element={
            <Require resource="banners" action="view">
              <Banners />
            </Require>
          }
        />
        <Route
          path="contents"
          element={
            <Require resource="contents" action="view">
              <Contents />
            </Require>
          }
        />

        {/* Etapas (duas telas) */}
        <Route
          path="pipeline-stages"
          element={
            <Require resource="pipeline_stages" action="view">
              <PipelineStages />
            </Require>
          }
        />
        <Route
          path="awareness-stages"
          element={
            <Require resource="awareness_stages" action="view">
              <AwarenessStages />
            </Require>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
